// Shared formatting helpers for the People & Group-detail pages.
//
// These deliberately stay in a tiny module — the v6 admin IA reuses the
// role-chip mapping and "X ago" relative-time everywhere, and we want one
// source of truth so a chip color tweak doesn't drift between pages.

import type { LocationScope } from "@/types/rule";

/**
 * Map a `role_id` to one of the v6 chip color modifiers (mockups/access-
 * preview-v6.html — `r-fleet`, `r-manager`, `r-viewer`, `r-investigator`,
 * `r-custom`).
 *
 * The mapping follows the visual grouping in the mockup, NOT the backend
 * `is_system` flag — `tenant_admin` and `fleet_admin` share the same red
 * chip because both grant tenant-wide power.
 */
export function roleClass(roleId: string): string {
  const id = roleId.toLowerCase();
  if (id === "fleet_admin" || id === "tenant_admin") return "r-fleet";
  if (id === "site_manager") return "r-manager";
  if (id === "site_viewer") return "r-viewer";
  if (id.includes("investigator")) return "r-investigator";
  return "r-custom";
}

/**
 * Render a backend ISO-8601 timestamp as a short "<n> <unit> ago" string.
 *
 * Returns "—" for unparseable / missing inputs so callers don't have to
 * branch. The thresholds (60s → 60m → 24h → ∞ days) match the mockup; we
 * deliberately don't go finer than minutes because relative-time UIs that
 * tick on seconds feel jittery.
 */
export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const diffMs = Date.now() - t;
  if (diffMs < 0) return "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}

/** True when a relative timestamp would render with the green "online" dot. */
export function isRecentlyActive(iso: string | undefined | null): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  const diffMs = Date.now() - t;
  return diffMs >= 0 && diffMs < 60 * 60 * 1000; // < 1 hour
}

/**
 * Build the human "scope" sentence fragment for a rule's location_scope.
 *
 *   - mode === "ALL"                  → "all <n> sites"
 *   - mode === "ALLOW", ids.length=1  → "<location_name>" (best-effort lookup,
 *                                       falls back to the raw id)
 *   - mode === "ALLOW" or "DENY", >1  → "<n> sites"
 *
 * `locationCount` is the tenant-wide site count (used only for the "all"
 * branch). `locationNameLookup` lets the caller plug in a friendly name
 * when one is known; if unset, the raw id is used.
 */
export function scopeSummary(
  scope: LocationScope,
  locationCount: number,
  locationNameLookup?: (id: string) => string | undefined,
): string {
  if (scope.mode === "ALL") {
    return locationCount > 0 ? `all ${locationCount} sites` : "all sites";
  }
  const ids = scope.ids;
  if (ids.length === 0) {
    return scope.mode === "DENY" ? "no sites" : "no sites";
  }
  if (ids.length === 1) {
    const id = ids[0];
    if (id === undefined) return "no sites";
    const name = locationNameLookup?.(id);
    return name ?? id;
  }
  return `${ids.length} sites`;
}
